<template>
  <div class="doudian-category-cascader">
    <div class="cascader-input" :class="{ 'is-small': size === 'small' }" @click="togglePanel">
      <span v-if="selectedCount > 0" class="cascader-value">已选择 {{ selectedCount }} 个分类</span>
      <span v-else class="cascader-placeholder">{{ placeholder }}</span>
      <i class="el-icon-arrow-down cascader-icon" :class="{ 'is-reverse': panelVisible }" />
    </div>
    <transition name="el-zoom-in-top">
      <div v-show="panelVisible" class="cascader-panel" :style="{ width: panelWidth }">
        <div class="cascader-search">
          <el-input
            v-model="searchKeyword"
            placeholder="搜索分类名称"
            clearable
            size="small"
            @input="handleSearch"
          />
        </div>
        <div class="cascader-columns">
          <div class="cascader-column">
            <div class="column-title">
              <el-checkbox
                :indeterminate="level1Indeterminate"
                :value="level1AllSelected"
                @change="toggleLevel1All"
              >
                一级分类
              </el-checkbox>
            </div>
            <div class="column-list">
              <div
                v-for="item in level1Categories"
                :key="item.category_id"
                class="category-item"
                :class="{ active: selectedLevel1 && selectedLevel1.category_id === item.category_id }"
                @click="handleLevel1Click(item)"
              >
                <el-checkbox
                  :value="isSelected(item.category_id)"
                  @click.native.prevent
                >
                  <span class="category-name">{{ item.category_name }}</span>
                </el-checkbox>
                <i v-if="item.children && item.children.length" class="el-icon-arrow-right" style="display: inline-block;"></i>
              </div>
              <div v-if="!level1Categories.length" class="empty-text">暂无分类</div>
            </div>
          </div>
          <div v-if="level2Categories.length" class="cascader-column">
            <div class="column-title">
              <el-checkbox
                :indeterminate="level2Indeterminate"
                :value="level2AllSelected"
                @change="toggleLevel2All"
              >
                二级分类
              </el-checkbox>
            </div>
            <div class="column-list">
              <div
                v-for="item in level2Categories"
                :key="item.category_id"
                class="category-item"
                :class="{ active: selectedLevel2 && selectedLevel2.category_id === item.category_id }"
                @click="handleLevel2Click(item)"
              >
                <el-checkbox
                  :value="isSelected(item.category_id)"
                  @click.native.prevent
                >
                  <span class="category-name">{{ item.category_name }}</span>
                </el-checkbox>
                <i v-if="item.children && item.children.length" class="el-icon-arrow-right" style="display: inline-block;"></i>
              </div>
            </div>
          </div>
          <div v-if="level3Categories.length" class="cascader-column">
            <div class="column-title">
              <el-checkbox
                :indeterminate="level3Indeterminate"
                :value="level3AllSelected"
                @change="toggleLevel3All"
              >
                三级分类
              </el-checkbox>
            </div>
            <div class="column-list">
              <div
                v-for="item in level3Categories"
                :key="item.category_id"
                class="category-item"
                @click="handleLevel3Click(item)"
              >
                <el-checkbox
                  :value="isSelected(item.category_id)"
                  @click.native.prevent
                >
                  <span class="category-name">{{ item.category_name }}</span>
                </el-checkbox>
              </div>
            </div>
          </div>
        </div>
        <div class="cascader-footer">
          <el-button size="small" @click="clearAll">清空</el-button>
          <el-button size="small" type="primary" @click="confirmSelection">确定</el-button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'DoudianCategoryCascader',
  props: {
    value: {
      type: Object,
      default: () => ({ level1: [], level2: [], level3: [] })
    },
    placeholder: {
      type: String,
      default: '请选择商品分类'
    },
    categories: {
      type: Array,
      default: () => []
    },
    maxSelected: {
      type: Number,
      default: 0
    },
    disabled: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      panelVisible: false,
      searchKeyword: '',
      selectedLevel1: null,
      selectedLevel2: null,
      selectedIds: [],
      selectedLevel1Ids: [],
      selectedLevel2Ids: [],
      selectedLevel3Ids: [],
      filteredCategories: []
    }
  },
  computed: {
    selectedCount() {
      if (!this.maxSelected) return this.selectedIds.length
      const selected = this.collectSelectedLeafCategories()
      return selected.level2.length + selected.level3.length
    },
    panelWidth() {
      if (this.level3Categories.length) return '750px'
      if (this.level2Categories.length) return '500px'
      return '250px'
    },
    level1Categories() {
      if (this.searchKeyword) {
        return this.filteredCategories
      }
      return this.categories || []
    },
    level2Categories() {
      return (this.selectedLevel1 && this.selectedLevel1.children) || []
    },
    level3Categories() {
      return (this.selectedLevel2 && this.selectedLevel2.children) || []
    },
    level1AllSelected() {
      const ids = this.level1Categories.map(item => item.category_id)
      return ids.length > 0 && ids.every(id => this.selectedIds.includes(id))
    },
    level1Indeterminate() {
      const ids = this.level1Categories.map(item => item.category_id)
      const selectedCount = ids.filter(id => this.selectedIds.includes(id)).length
      return selectedCount > 0 && selectedCount < ids.length
    },
    level2AllSelected() {
      const ids = this.level2Categories.map(item => item.category_id)
      return ids.length > 0 && ids.every(id => this.selectedIds.includes(id))
    },
    level2Indeterminate() {
      const ids = this.level2Categories.map(item => item.category_id)
      const selectedCount = ids.filter(id => this.selectedIds.includes(id)).length
      return selectedCount > 0 && selectedCount < ids.length
    },
    level3AllSelected() {
      const ids = this.level3Categories.map(item => item.category_id)
      return ids.length > 0 && ids.every(id => this.selectedIds.includes(id))
    },
    level3Indeterminate() {
      const ids = this.level3Categories.map(item => item.category_id)
      const selectedCount = ids.filter(id => this.selectedIds.includes(id)).length
      return selectedCount > 0 && selectedCount < ids.length
    }
  },
  watch: {
    value: {
      immediate: true,
      handler() {
        this.restoreSelectionFromValue()
      }
    },
    categories: {
      immediate: true,
      handler() {
        this.restoreSelectionFromValue()
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    togglePanel() {
      if (!this.disabled) this.panelVisible = !this.panelVisible
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.panelVisible = false
      }
    },
    handleSearch() {
      if (!this.searchKeyword) {
        this.filteredCategories = []
        return
      }
      const keyword = this.searchKeyword.toLowerCase()
      const roots = []
      let firstPath = null
      const search = (items, parents = []) => {
        items.forEach(item => {
          const path = [...parents, item]
          const name = (item.category_name || '').toLowerCase()
          if (name.includes(keyword)) {
            if (!roots.includes(path[0])) roots.push(path[0])
            if (!firstPath) firstPath = path
          }
          if (item.children && item.children.length) {
            search(item.children, path)
          }
        })
      }
      search(this.categories)
      this.filteredCategories = roots
      this.selectedLevel1 = firstPath ? firstPath[0] : null
      this.selectedLevel2 = firstPath ? (firstPath[1] || firstPath[0].children?.[0] || null) : null
    },
    selectLevel1(item) {
      this.selectedLevel1 = item
      this.selectedLevel2 = null
      if (!this.selectedIds.includes(item.category_id) && !this.canSelectCategories([item])) return
      // 自动勾选该分类
      if (!this.selectedIds.includes(item.category_id)) {
        this.selectedIds.push(item.category_id)
        this.selectAllChildren(item)
      }
    },
    selectLevel2(item) {
      this.selectedLevel2 = item
      if (!this.selectedIds.includes(item.category_id) && !this.canSelectCategories([item])) return
      // 自动勾选该分类
      if (!this.selectedIds.includes(item.category_id)) {
        this.selectedIds.push(item.category_id)
        this.selectAllChildren(item)
      }
    },
    handleLevel1Click(item) {
      // 展开子菜单
      this.selectedLevel1 = item
      this.selectedLevel2 = null
      // 切换选中状态
      if (!this.selectedIds.includes(item.category_id) && !this.canSelectCategories([item])) return
      const isCurrentlySelected = this.selectedIds.includes(item.category_id)
      if (isCurrentlySelected) {
        this.selectedIds = this.selectedIds.filter(id => id !== item.category_id)
        this.selectedLevel1Ids = this.selectedLevel1Ids.filter(id => id !== item.category_id)
        this.deselectAllChildren(item)
      } else {
        this.selectedIds.push(item.category_id)
        this.selectedLevel1Ids.push(item.category_id)
        this.selectAllChildren(item)
      }
    },
    handleLevel2Click(item) {
      // 展开子菜单
      this.selectedLevel2 = item
      // 切换选中状态
      if (!this.selectedIds.includes(item.category_id) && !this.canSelectCategories([item])) return
      const isCurrentlySelected = this.selectedIds.includes(item.category_id)
      if (isCurrentlySelected) {
        this.selectedIds = this.selectedIds.filter(id => id !== item.category_id)
        this.selectedLevel2Ids = this.selectedLevel2Ids.filter(id => id !== item.category_id)
        this.deselectAllChildren(item, 2)
        // 检查并取消父类目
        this.deselectParentIfNoChildren(item.category_id)
      } else {
        this.selectedIds.push(item.category_id)
        this.selectedLevel2Ids.push(item.category_id)
        // 选中父类目
        this.selectParentCategories(item.category_id)
        this.selectAllChildren(item, 2)
      }
    },
    handleLevel3Click(item) {
      // 切换选中状态
      if (!this.selectedIds.includes(item.category_id) && !this.canSelectCategories([item])) return
      const isCurrentlySelected = this.selectedIds.includes(item.category_id)
      if (isCurrentlySelected) {
        this.selectedIds = this.selectedIds.filter(id => id !== item.category_id)
        this.selectedLevel3Ids = this.selectedLevel3Ids.filter(id => id !== item.category_id)
        this.deselectAllChildren(item, 3)
        // 检查并取消父类目
        this.deselectParentIfNoChildren(item.category_id)
      } else {
        this.selectedIds.push(item.category_id)
        this.selectedLevel3Ids.push(item.category_id)
        // 选中父类目
        this.selectParentCategories(item.category_id)
        this.selectAllChildren(item, 3)
      }
    },
    isSelected(categoryId) {
      return this.selectedIds.includes(categoryId)
    },
    toggleSelect(categoryId, checked) {
      const item = this.findCategoryById(categoryId)
      if (!item) return

      if (checked && !this.canSelectCategories([item])) return
      if (checked) {
        if (!this.selectedIds.includes(categoryId)) {
          this.selectedIds.push(categoryId)
        }
        this.selectAllChildren(item)
      } else {
        this.selectedIds = this.selectedIds.filter(id => id !== categoryId)
        this.deselectAllChildren(item)
      }
    },
    restoreSelectionFromValue() {
      const value = this.value && typeof this.value === 'object' && !Array.isArray(this.value)
        ? this.value
        : {}
      const ids = [
        ...(value.level1 || []),
        ...(value.level2 || []),
        ...(value.level3 || [])
      ].map(id => String(id))
      const selectedIds = []
      const selectedLevel1Ids = []
      const selectedLevel2Ids = []
      const selectedLevel3Ids = []
      let firstPath = null
      const pushUnique = (list, id) => {
        if (!list.some(item => String(item) === String(id))) list.push(id)
      }
      const findPath = (items, targetId, parents = []) => {
        for (const item of items || []) {
          const path = [...parents, item]
          if (String(item.category_id) === targetId) return path
          const found = findPath(item.children, targetId, path)
          if (found) return found
        }
        return null
      }
      ids.forEach(id => {
        const path = findPath(this.categories, id)
        if (!path || (path[path.length - 1].children || []).length) return
        path.forEach((item, index) => {
          pushUnique(selectedIds, item.category_id)
          if (index === 0) pushUnique(selectedLevel1Ids, item.category_id)
          if (index === 1) pushUnique(selectedLevel2Ids, item.category_id)
          if (index === 2) pushUnique(selectedLevel3Ids, item.category_id)
        })
        if (!firstPath) firstPath = path
      })
      this.selectedIds = selectedIds
      this.selectedLevel1Ids = selectedLevel1Ids
      this.selectedLevel2Ids = selectedLevel2Ids
      this.selectedLevel3Ids = selectedLevel3Ids
      this.selectedLevel1 = firstPath ? firstPath[0] : null
      this.selectedLevel2 = firstPath && firstPath.length > 1 ? firstPath[1] : null
    },
    findCategoryById(categoryId, items = this.categories) {
      for (const item of items) {
        if (item.category_id === categoryId) {
          return item
        }
        if (item.children && item.children.length) {
          const found = this.findCategoryById(categoryId, item.children)
          if (found) return found
        }
      }
      return null
    },
    findParentCategories(categoryId) {
      const parents = []
      const findParent = (items, targetId, currentParents = []) => {
        for (const item of items) {
          if (item.category_id === targetId) {
            return currentParents
          }
          if (item.children && item.children.length) {
            const result = findParent(item.children, targetId, [...currentParents, item])
            if (result) return result
          }
        }
        return null
      }
      return findParent(this.categories, categoryId) || []
    },
    getCategoryLevel(categoryId) {
      const parents = this.findParentCategories(categoryId)
      return parents.length + 1 // 父级数量+1就是当前级别
    },
    selectParentCategories(categoryId) {
      const parents = this.findParentCategories(categoryId)
      parents.forEach((parent, index) => {
        const level = index + 1
        if (!this.selectedIds.includes(parent.category_id)) {
          this.selectedIds.push(parent.category_id)
          if (level === 1) {
            if (!this.selectedLevel1Ids.includes(parent.category_id)) {
              this.selectedLevel1Ids.push(parent.category_id)
            }
          } else if (level === 2) {
            if (!this.selectedLevel2Ids.includes(parent.category_id)) {
              this.selectedLevel2Ids.push(parent.category_id)
            }
          }
        }
      })
    },
    hasSelectedChildren(item) {
      if (!item.children || !item.children.length) {
        return false
      }
      for (const child of item.children) {
        if (this.selectedIds.includes(child.category_id)) {
          return true
        }
        if (this.hasSelectedChildren(child)) {
          return true
        }
      }
      return false
    },
    deselectParentIfNoChildren(categoryId) {
      const parents = this.findParentCategories(categoryId)
      parents.forEach((parent, index) => {
        const level = index + 1
        // 检查父类目是否还有其他选中的子类目
        if (!this.hasSelectedChildren(parent)) {
          this.selectedIds = this.selectedIds.filter(id => id !== parent.category_id)
          if (level === 1) {
            this.selectedLevel1Ids = this.selectedLevel1Ids.filter(id => id !== parent.category_id)
          } else if (level === 2) {
            this.selectedLevel2Ids = this.selectedLevel2Ids.filter(id => id !== parent.category_id)
          }
        }
      })
    },
    selectAllChildren(item, parentLevel = 0) {
      if (item.children && item.children.length) {
        const currentLevel = parentLevel + 1
        item.children.forEach(child => {
          if (!this.selectedIds.includes(child.category_id)) {
            this.selectedIds.push(child.category_id)
            // 根据级别添加到对应数组
            if (currentLevel === 1) {
              if (!this.selectedLevel1Ids.includes(child.category_id)) {
                this.selectedLevel1Ids.push(child.category_id)
              }
            } else if (currentLevel === 2) {
              if (!this.selectedLevel2Ids.includes(child.category_id)) {
                this.selectedLevel2Ids.push(child.category_id)
              }
            } else if (currentLevel === 3) {
              if (!this.selectedLevel3Ids.includes(child.category_id)) {
                this.selectedLevel3Ids.push(child.category_id)
              }
            }
          }
          this.selectAllChildren(child, currentLevel)
        })
      }
    },
    deselectAllChildren(item, parentLevel = 0) {
      if (item.children && item.children.length) {
        const currentLevel = parentLevel + 1
        item.children.forEach(child => {
          this.selectedIds = this.selectedIds.filter(id => id !== child.category_id)
          // 从对应级别数组中移除
          if (currentLevel === 1) {
            this.selectedLevel1Ids = this.selectedLevel1Ids.filter(id => id !== child.category_id)
          } else if (currentLevel === 2) {
            this.selectedLevel2Ids = this.selectedLevel2Ids.filter(id => id !== child.category_id)
          } else if (currentLevel === 3) {
            this.selectedLevel3Ids = this.selectedLevel3Ids.filter(id => id !== child.category_id)
          }
          this.deselectAllChildren(child, currentLevel)
        })
      }
    },
    toggleLevel1All(checked) {
      const items = this.level1Categories
      if (checked && !this.canSelectCategories(items)) return
      if (checked) {
        items.forEach(item => {
          if (!this.selectedIds.includes(item.category_id)) {
            this.selectedIds.push(item.category_id)
            this.selectedLevel1Ids.push(item.category_id)
          }
          this.selectAllChildren(item, 1)
        })
      } else {
        items.forEach(item => {
          this.selectedIds = this.selectedIds.filter(id => id !== item.category_id)
          this.selectedLevel1Ids = this.selectedLevel1Ids.filter(id => id !== item.category_id)
          this.deselectAllChildren(item, 1)
        })
      }
    },
    toggleLevel2All(checked) {
      const items = this.level2Categories
      if (checked && !this.canSelectCategories(items)) return
      if (checked) {
        items.forEach(item => {
          if (!this.selectedIds.includes(item.category_id)) {
            this.selectedIds.push(item.category_id)
            this.selectedLevel2Ids.push(item.category_id)
          }
          // 选中父类目
          this.selectParentCategories(item.category_id)
          this.selectAllChildren(item, 2)
        })
      } else {
        items.forEach(item => {
          this.selectedIds = this.selectedIds.filter(id => id !== item.category_id)
          this.selectedLevel2Ids = this.selectedLevel2Ids.filter(id => id !== item.category_id)
          this.deselectAllChildren(item, 2)
        })
        // 检查并取消父类目
        if (this.selectedLevel1 && items.length > 0) {
          this.deselectParentIfNoChildren(items[0].category_id)
        }
      }
    },
    toggleLevel3All(checked) {
      const items = this.level3Categories
      if (checked && !this.canSelectCategories(items)) return
      if (checked) {
        items.forEach(item => {
          if (!this.selectedIds.includes(item.category_id)) {
            this.selectedIds.push(item.category_id)
            this.selectedLevel3Ids.push(item.category_id)
          }
          // 选中父类目
          this.selectParentCategories(item.category_id)
          this.selectAllChildren(item, 3)
        })
      } else {
        items.forEach(item => {
          this.selectedIds = this.selectedIds.filter(id => id !== item.category_id)
          this.selectedLevel3Ids = this.selectedLevel3Ids.filter(id => id !== item.category_id)
          this.deselectAllChildren(item, 3)
        })
        // 检查并取消父类目
        if (this.selectedLevel2 && items.length > 0) {
          this.deselectParentIfNoChildren(items[0].category_id)
        }
      }
    },
    clearAll() {
      this.selectedIds = []
      this.selectedLevel1Ids = []
      this.selectedLevel2Ids = []
      this.selectedLevel3Ids = []
      this.selectedLevel1 = null
      this.selectedLevel2 = null
      const result = { level1: [], level2: [], level3: [] }
      this.$emit('input', result)
      this.$emit('change', result)
    },
    canSelectCategories(items) {
      if (!this.maxSelected) return true
      const ids = new Set(this.selectedIds)
      const collect = nodes => nodes.forEach(item => {
        ids.add(item.category_id)
        collect(item.children || [])
      })
      collect(items)
      const selected = this.collectSelectedLeafCategories([...ids])
      if (selected.level2.length + selected.level3.length <= this.maxSelected) return true
      this.$message.warning(`二级、三级子类目合计最多选择${this.maxSelected}个`)
      return false
    },
    collectSelectedLeafCategories(selectedIds = this.selectedIds) {
      const result = { level1: [], level2: [], level3: [] }
      const walk = (items, level) => {
        ;(items || []).forEach(item => {
          const children = Array.isArray(item.children) ? item.children : []
          if (children.length) {
            walk(children, level + 1)
          } else if (selectedIds.includes(item.category_id) && level <= 3) {
            result[`level${level}`].push(item.category_id)
          }
        })
      }
      walk(this.categories, 1)
      return result
    },
    confirmSelection() {
      if (!this.canSelectCategories([])) return
      const result = this.collectSelectedLeafCategories()
      this.$emit('input', result)
      this.$emit('change', result)
      this.panelVisible = false
      this.searchKeyword = ''
      this.filteredCategories = []
    }
  }
}
</script>

<style scoped>
.doudian-category-cascader {
  position: relative;
  width: 100%;
}
.cascader-input {
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  width: 100%;
  height: 28px;
  padding: 4px 8px;
  border: 1px solid #e0e0ec;
  border-radius: 4px;
  background: #fff;
  font-size: 11px;
  cursor: pointer;
  transition: border-color 0.2s;
}
.cascader-input.is-small { height: 32px; font-size: 11px; }
.cascader-input:hover {
  border-color: #c0c4cc;
}
.cascader-value {
  flex: 1;
  overflow: hidden;
  color: #1a1a2e;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cascader-placeholder {
  flex: 1;
  color: #999;
}
.cascader-icon {
  margin-left: 5px;
  color: #999;
  font-size: 12px;
  transition: transform 0.3s;
}
.cascader-icon.is-reverse {
  transform: rotate(180deg);
}
.cascader-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 4001;
  border: 1px solid #e0e0ec;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  transition: width 0.3s ease;
  overflow-x: hidden;
}
.cascader-search {
  padding: 10px;
  border-bottom: 1px solid #e0e0ec;
  background: #fafafa;
}
.cascader-columns {
  display: flex;
  min-height: 300px;
  max-height: 400px;
}
.cascader-column {
  flex: 0 0 250px;
  width: 250px;
  border-right: 1px solid #e0e0ec;
  display: flex;
  flex-direction: column;
}
.cascader-column:last-child {
  border-right: 0;
}
.column-title {
  padding: 8px 12px;
  border-bottom: 1px solid #e0e0ec;
  background: #f5f6f8;
  font-size: 11px;
  font-weight: 600;
}
.column-title >>> .el-checkbox__label {
  font-weight: 600;
  color: #666;
}
.column-list {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}
.category-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  min-height: 34px;
  padding: 8px 12px 8px 12px;
  color: #1a1a2e;
  font-size: 11px;
  cursor: pointer;
  transition: background-color 0.2s;
  gap: 8px;
  box-sizing: border-box;
}
.category-item:hover {
  background: #f5f6f8;
}
.category-item.active {
  background: #e6f4ff;
}
.category-item >>> .el-checkbox {
  flex: 1;
  margin-right: 0;
  display: flex;
  align-items: flex-start;
  min-width: 0;
}
.category-item >>> .el-checkbox__input {
  flex: 0 0 auto;
  line-height: 1;
  margin-top: 2px;
}
.category-item >>> .el-checkbox__label {
  flex: 1;
  overflow: visible;
  text-overflow: clip;
  white-space: normal;
  color: #1a1a2e;
  font-size: 11px;
  padding-left: 8px;
  line-height: 16px;
}
.category-item.active >>> .el-checkbox__label {
  color: #1677ff;
  font-weight: 500;
}
.category-name {
  flex: 1;
  white-space: normal;
  font-size: 12px;
}
.category-item .el-icon-arrow-right,
.category-item .arrow-icon {
  margin-left: 5px;
  color: #999;
  font-size: 12px;
  flex-shrink: 0;
  margin-top: 2px;
  display: inline-block;
}
.category-item.active .el-icon-arrow-right,
.category-item.active .arrow-icon {
  color: #1677ff;
}
.empty-text {
  padding: 30px 12px;
  color: #999;
  font-size: 11px;
  text-align: center;
}
.cascader-footer {
  padding: 10px;
  border-top: 1px solid #e0e0ec;
  background: #fafafa;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
